import React, { useState, useRef, useEffect } from 'react';
import { C } from '../utils/styles';
import Icon from './Icon';

/**
 * StyledSelect - A custom select dropdown that matches the app's design system
 * Used as a replacement for native <select> elements
 */
export default function StyledSelect({ 
  value, 
  onValueChange, 
  options = [], 
  placeholder = "Select...",
  disabled = false,
  style = {},
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (optionValue) => {
    onValueChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%', ...style }} ref={dropdownRef} className={className}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: C.card,
          border: `1px solid ${isOpen ? C.accent : C.border}`,
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          color: value ? C.text : C.textLight,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          boxShadow: isOpen ? `0 0 0 3px rgba(0, 102, 204, 0.1)` : 'none',
          transition: 'all 0.15s ease',
          outline: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayText}
        </span>
        <Icon name={isOpen ? "chevron-up" : "chevron-down"} style={{ fontSize: 14, color: C.textMid, flexShrink: 0, marginLeft: 8 }} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          zIndex: 1000,
          maxHeight: 300,
          overflowY: 'auto',
          padding: 4,
        }}>
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  background: isSelected ? C.accentLight : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? C.accent : C.text,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.1s ease',
                  outline: 'none',
                }}
                onMouseEnter={(e) => !isSelected && (e.currentTarget.style.background = C.bg)}
                onMouseLeave={(e) => !isSelected && (e.currentTarget.style.background = 'transparent')}
              >
                {isSelected && (
                  <Icon name="checkmark" style={{ fontSize: 16, color: C.accent, flexShrink: 0 }} />
                )}
                <span style={{ marginLeft: isSelected ? 0 : 24, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
